import dayjs from "dayjs";
import { shrpaApi } from "./axios";
import { getCalendarId } from "@logic/calendarManager";
import {
  BulkImportSettingsResponse,
  CompleteContentIngestionGroupResponse,
  ContentIngestionAnalyzeRequestOptions,
  ContentIngestionAnalyzeResponse,
  ContentIngestionManifestFileInput,
  FinalizeContentIngestionResponse,
  SaveContentIngestionGroupRequest,
  SaveContentIngestionGroupResponse,
} from "@contracts/contentIngestion";

/** Defaults match [BulkImageIngestionController] manifest constants if the server omits values. */
const DEFAULT_MANIFEST_LINE_SEPARATOR = "\n";
const DEFAULT_MANIFEST_FIELD_SEPARATOR = "|";

export default {
  async getBulkImportSettings(customerId: string): Promise<BulkImportSettingsResponse> {
    const uri = `/content-calendar-ingestion/${customerId}/import-settings`;
    const { data } = await shrpaApi.get<BulkImportSettingsResponse>(uri);
    return data;
  },

  async analyze(
    customerId: string,
    files: Array<ContentIngestionManifestFileInput>,
    bulkImportSettings: BulkImportSettingsResponse,
    options: ContentIngestionAnalyzeRequestOptions = {}
  ): Promise<ContentIngestionAnalyzeResponse> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-ingestion/${customerId}/${calendarId}/analyze`;
    const lineSep = bulkImportSettings.manifestLineSeparator || DEFAULT_MANIFEST_LINE_SEPARATOR;
    const fieldSep = bulkImportSettings.manifestFieldSeparator || DEFAULT_MANIFEST_FIELD_SEPARATOR;
    const filesPayload = files.map(f => `${f.relativePath}${fieldSep}${f.size}`).join(lineSep);
    const payload = {
      files: filesPayload,
      groupingMode: options.groupingMode,
      groupingGuidance: options.groupingGuidance || undefined,
      singleGroupName: options.singleGroupName || undefined,
    };
    const { data } = await shrpaApi.post<ContentIngestionAnalyzeResponse>(uri, payload);
    return data;
  },

  async saveGroup(customerId: string, groupId: string, body: Omit<SaveContentIngestionGroupRequest, "clientSideDateTime">): Promise<SaveContentIngestionGroupResponse> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-ingestion/${customerId}/${calendarId}/groups/${encodeURIComponent(groupId)}`;
    const payload: SaveContentIngestionGroupRequest = {
      ...body,
      clientSideDateTime: dayjs().format(),
    };
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.put<SaveContentIngestionGroupResponse>(uri, JSON.stringify(payload), config);
    return data;
  },

  async completeGroup(customerId: string, groupId: string, body: Omit<SaveContentIngestionGroupRequest, "clientSideDateTime">): Promise<CompleteContentIngestionGroupResponse> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-ingestion/${customerId}/${calendarId}/groups/${encodeURIComponent(groupId)}/complete`;
    const payload: SaveContentIngestionGroupRequest = {
      ...body,
      clientSideDateTime: dayjs().format(),
    };
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.post<CompleteContentIngestionGroupResponse>(uri, JSON.stringify(payload), config);
    return data;
  },

  async finalize(customerId: string, bulkUploadSessionId: string | null): Promise<FinalizeContentIngestionResponse> {
    const calendarId = await getCalendarId(customerId);
    const uri = `/content-calendar-ingestion/${customerId}/${calendarId}/finalize`;
    const payload = { bulkUploadSessionId: bulkUploadSessionId ?? undefined };
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await shrpaApi.post<FinalizeContentIngestionResponse>(uri, JSON.stringify(payload), config);
    return data;
  },
};
